import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import API from '../../util/api';
import Greeting from '../../components/Dashboard/Greeting';
import DashboardMenu from '../../components/Dashboard/DashboardMenu';
import DashboardResources from '../../components/Dashboard/DashboardResources';
import CurriculumBanner from '../../components/Dashboard/CurriculumBanner';
import CurriculumDownload from '../../components/Dashboard/CurriculumDownload';
import ImplementationsRequiringCompletion from '../../components/Dashboard/ImplementationsRequiringCompletion';
import useEffectiveTeacherIdentity from '../../hooks/useEffectiveTeacherIdentity';
import useCurriculumLink from '../../hooks/useCurriculumLink';
import { actions } from '../../util/actions';
import {
  parseDateValue,
  normalizeTeacherImplementations,
  gradeSortValue,
  selectPreferredGrade,
} from '../../util/teacherImplementations';

const breakpoints = {
  tablet: 768,
  phone:  480,
};
const DEFAULT_GRADE_CODES = ["K","1","2","3","4","5","6","7","8","9","10","11","12"];
const TEACHER_IMPL_CACHE_TTL_MS = 60 * 1000;


const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  min-height: 100vh;

  /* tablet and below */
  @media (max-width: ${breakpoints.tablet}px) {
    padding: 16px;
  }
  /* phone and below */
  @media (max-width: ${breakpoints.phone}px) {
    padding: 12px;
    align-items: center;
  }
`;

const ResourcesContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 25%) minmax(0, 75%);
  gap: 20px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  align-items: flex-start;

  /* tablet and below: tighten gaps */
  @media (max-width: ${breakpoints.tablet}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  /* phone and below: single column */
  @media (max-width: ${breakpoints.phone}px) {
    gap: 12px;
    padding: 0 10px;
  }
`;

const CurriculumContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  /* phone and below: full width */
  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

const SectionGap = styled.div`
  height: 2rem;

  /* tablet and below */
  @media (max-width: ${breakpoints.tablet}px) {
    height: 1.5rem;
  }
  /* phone and below */
  @media (max-width: ${breakpoints.phone}px) {
    height: 1rem;
  }
`;


export default function DashboardPage({ colors }) {
  const { customerId, teacherId } = useEffectiveTeacherIdentity();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state?.auth?.user) || {};
  const userRoles = authUser?.roles || [];
  const isSubscriptionAdmin = userRoles.some(
    (role) => role?.id === "subscription_admin"
  );
  const userSessionId = authUser?.session_id ?? null;
  console.log(userSessionId)
  const authUserId = authUser?.user_id ?? null;
  console.log(isSubscriptionAdmin)
  const selectedSubscription =
    useSelector((state) => state?.auth?.selected_subscriptions) || {};
  const cachedImplementations = useSelector(
    (state) => state?.auth?.teacher_implementations || []
  );
  const teacherImplementationsFetchedAt = useSelector(
    (state) => state?.auth?.teacher_implementations_fetched_at ?? null
  );
  const teacherImplementationsLoading = useSelector(
    (state) => state?.auth?.teacher_implementations_loading ?? false
  );
  const [teacherImplementations, setTeacherImplementations] = useState(() => cachedImplementations);
  const [implementationsLoading, setImplementationsLoading] = useState(
    () => cachedImplementations.length === 0
  );
  const [hasLoadedImplementations, setHasLoadedImplementations] = useState(
    () => cachedImplementations.length > 0
  );
  console.log("Teachers Implementations", teacherImplementations)
  const tabActiveRef = useRef(
    typeof document !== 'undefined' && document.visibilityState === 'visible'
  );
  const cachedCountRef = useRef(cachedImplementations.length);
  const fetchedAtRef = useRef(teacherImplementationsFetchedAt);
  const loadingRef = useRef(teacherImplementationsLoading);
  const inFlightRef = useRef(false);
  const forceReloadFetchRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const navEntry = window.performance
      ?.getEntriesByType?.("navigation")
      ?.at?.(0);
    const isReloadNavigation =
      navEntry?.type === "reload" || window.performance?.navigation?.type === 1;

    forceReloadFetchRef.current = Boolean(isReloadNavigation);
  }, []);

  useEffect(() => {
    cachedCountRef.current = cachedImplementations.length;
  }, [cachedImplementations.length]);

  useEffect(() => {
    fetchedAtRef.current = teacherImplementationsFetchedAt;
  }, [teacherImplementationsFetchedAt]);

  useEffect(() => {
    loadingRef.current = teacherImplementationsLoading;
  }, [teacherImplementationsLoading]);

  const fetchImpl = useCallback(
    (forceRefresh = false) => {
      if (!customerId || !teacherId) return;
      if (inFlightRef.current) return;
      if (!forceRefresh && loadingRef.current) {
        setImplementationsLoading(true);
        return;
      }
      const now = Date.now();
      const cacheFresh =
        !forceRefresh &&
        fetchedAtRef.current &&
        now - fetchedAtRef.current < TEACHER_IMPL_CACHE_TTL_MS;

      if (cacheFresh) {
        if (loadingRef.current) {
          setImplementationsLoading(true);
        } else {
          setHasLoadedImplementations(true);
          setImplementationsLoading(false);
        }
        return;
      }
      const shouldShowLoading = cachedCountRef.current === 0 && !forceRefresh;
      if (shouldShowLoading) {
        setImplementationsLoading(true);
      }

      inFlightRef.current = true;
      dispatch({
        type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
        loading: true,
      });
      dispatch({
        type: actions.SET_TEACHER_IMPLEMENTATIONS_FETCHED_AT,
        fetchedAt: Date.now(),
      });
      API.getTeacherImplementationsNew({ customer_id: customerId, teacher_id: teacherId })
        .then((resp) => {
          if (resp.status === 'Success') {
            const details = Array.isArray(resp.detail) ? resp.detail : [];
            const normalized = normalizeTeacherImplementations(details, teacherId);

            setTeacherImplementations(normalized);
            setHasLoadedImplementations(true);
            dispatch({
              type: actions.STORE_TEACHER_IMPLEMENTATIONS,
              implementations: normalized,
              fetchedAt: Date.now(),
            });
          } else {
            setTeacherImplementations([]);
            setHasLoadedImplementations(true);
            dispatch({
              type: actions.STORE_TEACHER_IMPLEMENTATIONS,
              implementations: [],
              fetchedAt: Date.now(),
            });
          }
        })
        .catch((err) =>
          console.error('Failed to load teacher implementations:', err)
        )
        .finally(() => {
          inFlightRef.current = false;
          setImplementationsLoading(false);
          dispatch({
            type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
            loading: false,
          });
        });
    },
    [customerId, teacherId, dispatch]
  );

  useEffect(() => {
    if (Array.isArray(cachedImplementations)) {
      setTeacherImplementations(cachedImplementations);
      if (cachedImplementations.length > 0) {
        setHasLoadedImplementations(true);
        setImplementationsLoading(false);
      }
    }
  }, [cachedImplementations]);

  useEffect(() => {
    if (!teacherImplementationsLoading && teacherImplementationsFetchedAt) {
      setHasLoadedImplementations(true);
      setImplementationsLoading(false);
    }
  }, [teacherImplementationsLoading, teacherImplementationsFetchedAt]);

  useEffect(() => {
    if (!forceReloadFetchRef.current) return;
    if (!customerId || !teacherId) return;

    forceReloadFetchRef.current = false;
    fetchImpl(true);
  }, [customerId, teacherId, fetchImpl]);

  useEffect(() => {
    const now = Date.now();
    const cacheFresh =
      fetchedAtRef.current &&
      now - fetchedAtRef.current < TEACHER_IMPL_CACHE_TTL_MS;

    if (!cacheFresh && !teacherImplementationsLoading) {
      fetchImpl();
    }
  }, [fetchImpl, teacherImplementationsLoading]);

  useEffect(() => {
    // handler to refetch when tab becomes visible or window regains focus
    const handleVisibilityChange = () => {
      const isVisible =
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible';
      if (isVisible && !tabActiveRef.current) {
        fetchImpl(true);
      }
      if (typeof document !== 'undefined') {
        tabActiveRef.current = isVisible;
      }
    };

    const handleWindowFocus = () => {
      const isVisible =
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible';
      if (isVisible && !tabActiveRef.current) {
        fetchImpl(true);
      }
      if (typeof document !== 'undefined') {
        tabActiveRef.current = isVisible;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [fetchImpl]);

  const downloadImplementation = useMemo(() => {
    const hasImplementations =
      Array.isArray(teacherImplementations) && teacherImplementations.length > 0;

    const buildAdminFallback = () => {
      if (!isSubscriptionAdmin) return null;
      const derivedUserId = teacherId ?? authUserId ?? null;
      if (!derivedUserId || !userSessionId) return null;
      const derivedSchoolId =
        (Array.isArray(teacherImplementations)
          ? teacherImplementations.find((impl) => impl.school_id)?.school_id
          : null) ??
        selectedSubscription?.school_id ??
        null;
      const fallbackGrades = DEFAULT_GRADE_CODES.map((code) => ({
        grade_code: code,
        user_id: derivedUserId,
        session_id: userSessionId,
        school_id: derivedSchoolId,
        status: "0",
        status1: "0",
      }));
      return {
        user_id: derivedUserId,
        session_id: userSessionId,
        school_id: derivedSchoolId,
        grade_codes: fallbackGrades,
      };
    };

    if (!hasImplementations) {
      return buildAdminFallback();
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const gradeMap = new Map();

    teacherImplementations.forEach((impl) => {
      const implEndDate = parseDateValue(impl?.end_date);
      const implementationActive = !implEndDate || implEndDate.getTime() >= today.getTime();

      impl.grade_codes.forEach((grade) => {
        const code = String(grade?.grade_code ?? '').trim();
        if (!code) return;

        const gradeEndDate = parseDateValue(grade?.end_date ?? impl?.end_date);
        if (gradeEndDate && gradeEndDate.getTime() < today.getTime()) {
          return;
        }
        if (!implementationActive && !gradeEndDate) {
          return;
        }

        const candidate = {
          ...grade,
          grade_code: code,
          session_id: grade.session_id ?? impl.session_id ?? null,
          user_id: grade.user_id ?? impl.user_id ?? null,
          school_id: grade.school_id ?? impl.school_id ?? null,
          implementation_id: impl.implementation_id,
          implementation_end_date: impl.end_date ?? null,
        };

        const existing = gradeMap.get(code);
        gradeMap.set(code, selectPreferredGrade(existing, candidate));
      });
    });

    const gradeList = Array.from(gradeMap.values()).sort((a, b) => {
      const aKey = gradeSortValue(a.grade_code);
      const bKey = gradeSortValue(b.grade_code);
      if (aKey !== bKey) return aKey - bKey;
      return String(a.grade_code ?? '').localeCompare(String(b.grade_code ?? ''));
    });

    if (gradeList.length === 0) {
      return buildAdminFallback();
    }

    const fallback = gradeList.find((g) => g.user_id && g.session_id) ?? gradeList[0];
    const fallbackUserId =
      fallback.user_id ??
      teacherImplementations.find((impl) => impl.user_id)?.user_id ??
      teacherId ??
      authUserId ??
      null;
    const fallbackSessionId =
      fallback.session_id ??
      teacherImplementations.find((impl) => impl.session_id)?.session_id ??
      (isSubscriptionAdmin ? userSessionId : null) ??
      null;
    const fallbackSchoolId =
      fallback.school_id ??
      teacherImplementations.find((impl) => impl.school_id)?.school_id ??
      selectedSubscription?.school_id ??
      null;

    return {
      user_id: fallbackUserId,
      session_id: fallbackSessionId,
      school_id: fallbackSchoolId,
      grade_codes: gradeList,
    };
  }, [
    authUserId,
    isSubscriptionAdmin,
    selectedSubscription?.school_id,
    teacherId,
    teacherImplementations,
    userSessionId,
  ]);

  const showInitialLoading = implementationsLoading && !hasLoadedImplementations;

  const curriculumLink = useCurriculumLink();

  return (
    <DashboardContainer>
      <ResourcesContainer>
        <DashboardResources colors={colors} curriculumLink={curriculumLink} />
        <CurriculumContainer>
          <CurriculumBanner curriculumLink={curriculumLink} />
          <CurriculumDownload
            colors={colors}
            implementation={downloadImplementation}
            isLoading={showInitialLoading}
            onRefresh={fetchImpl}
            curriculumLink={curriculumLink}
          />
          <SectionGap />
          <ImplementationsRequiringCompletion
            colors={colors}
            implementations={teacherImplementations}
            isLoading={showInitialLoading}
            onRefresh={fetchImpl}
          />
        </CurriculumContainer>
      </ResourcesContainer>
    </DashboardContainer>
  );
}
